import { useEffect, useState, type FormEvent } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import Field, { inputClass } from '@/components/base/Field';

export default function SettingsPage() {
  const { profile, refreshProfile } = useAuth();

  const [name, setName] = useState('');
  const [nameBusy, setNameBusy] = useState(false);
  const [nameNotice, setNameNotice] = useState('');
  const [nameError, setNameError] = useState('');

  const [pw1, setPw1] = useState('');
  const [pw2, setPw2] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [pwBusy, setPwBusy] = useState(false);
  const [pwNotice, setPwNotice] = useState('');
  const [pwError, setPwError] = useState('');

  useEffect(() => {
    setName(profile?.name ?? '');
  }, [profile?.name]);

  useEffect(() => {
    if (!nameNotice) return;
    const t = window.setTimeout(() => setNameNotice(''), 2500);
    return () => window.clearTimeout(t);
  }, [nameNotice]);

  useEffect(() => {
    if (!pwNotice) return;
    const t = window.setTimeout(() => setPwNotice(''), 2500);
    return () => window.clearTimeout(t);
  }, [pwNotice]);

  const handleNameSave = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setNameError('');
    setNameNotice('');
    const trimmed = name.trim();
    if (!trimmed) {
      setNameError('이름을 입력해주세요.');
      return;
    }
    if (!profile) return;
    setNameBusy(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ name: trimmed })
        .eq('id', profile.id);
      if (error) throw error;
      await refreshProfile();
      setNameNotice('이름을 저장했어요.');
    } catch {
      setNameError('이름 저장에 실패했어요. 잠시 후 다시 시도해주세요.');
    } finally {
      setNameBusy(false);
    }
  };

  const handlePasswordSave = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setPwError('');
    setPwNotice('');
    if (pw1.length < 6) {
      setPwError('비밀번호는 6자 이상으로 입력해주세요.');
      return;
    }
    if (pw1 !== pw2) {
      setPwError('두 비밀번호가 서로 달라요.');
      return;
    }
    setPwBusy(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: pw1 });
      if (error) throw error;
      setPw1('');
      setPw2('');
      setPwNotice('비밀번호를 변경했어요.');
    } catch {
      setPwError('비밀번호 변경에 실패했어요. 잠시 후 다시 시도해주세요.');
    } finally {
      setPwBusy(false);
    }
  };

  const roleLabel = profile?.role === 'admin' ? '운영자' : '선생님';
  const statusLabel =
    profile?.status === 'approved'
      ? '승인 완료'
      : profile?.status === 'pending'
        ? '승인 대기'
        : '승인 거절';

  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-8 md:px-6 md:py-10">
      <div>
        <h1 className="text-2xl font-bold text-foreground-950">내 정보</h1>
        <p className="mt-1.5 text-sm text-foreground-600">
          선생님 계정의 이름과 비밀번호를 관리해요.
        </p>
      </div>

      <section className="mt-6 rounded-xl border border-background-200 bg-background-50 p-5 md:p-6">
        <div className="flex items-center gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
            <i className="ri-user-line"></i>
          </span>
          <h2 className="text-base font-bold text-foreground-950">계정 정보</h2>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="rounded-lg border border-background-200 bg-background-100 p-3.5">
            <p className="text-[11px] text-foreground-500">이메일</p>
            <p className="mt-1 break-all text-sm font-semibold text-foreground-900">
              {profile?.email ?? '-'}
            </p>
          </div>
          <div className="rounded-lg border border-background-200 bg-background-100 p-3.5">
            <p className="text-[11px] text-foreground-500">역할</p>
            <p className="mt-1 text-sm font-semibold text-foreground-900">{roleLabel}</p>
          </div>
          <div className="rounded-lg border border-background-200 bg-background-100 p-3.5">
            <p className="text-[11px] text-foreground-500">가입 상태</p>
            <p className="mt-1 text-sm font-semibold text-foreground-900">{statusLabel}</p>
          </div>
        </div>

        <form onSubmit={handleNameSave} className="mt-5 flex flex-col gap-4">
          <Field label="이름" hint="앱 상단과 리포트에 표시되는 선생님 이름이에요.">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="이름을 입력하세요"
              className={inputClass}
            />
          </Field>

          {nameError && (
            <div className="flex items-start gap-2 rounded-md bg-accent-100 px-3 py-2.5 text-sm text-accent-900">
              <i className="ri-error-warning-line mt-0.5"></i>
              <span>{nameError}</span>
            </div>
          )}
          {nameNotice && (
            <div className="flex items-start gap-2 rounded-md bg-primary-100 px-3 py-2.5 text-sm text-primary-700">
              <i className="ri-checkbox-circle-line mt-0.5"></i>
              <span>{nameNotice}</span>
            </div>
          )}

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={nameBusy}
              className="flex items-center gap-1.5 rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition hover:bg-primary-600 disabled:opacity-50 whitespace-nowrap"
            >
              {nameBusy ? (
                <i className="ri-loader-4-line animate-spin"></i>
              ) : (
                <i className="ri-save-line"></i>
              )}
              이름 저장
            </button>
          </div>
        </form>
      </section>

      <section className="mt-5 rounded-xl border border-background-200 bg-background-50 p-5 md:p-6">
        <div className="flex items-center gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-secondary-100 text-secondary-800">
            <i className="ri-lock-password-line"></i>
          </span>
          <h2 className="text-base font-bold text-foreground-950">비밀번호 변경</h2>
        </div>
        <p className="mt-1.5 text-xs leading-relaxed text-foreground-500">
          새 비밀번호는 6자 이상으로 입력해주세요. 변경하면 다음 로그인부터 새 비밀번호를 사용해요.
        </p>

        <form onSubmit={handlePasswordSave} className="mt-4 flex flex-col gap-4">
          <Field label="새 비밀번호" required>
            <div className="relative">
              <input
                type={showPw ? 'text' : 'password'}
                autoComplete="new-password"
                value={pw1}
                onChange={(e) => setPw1(e.target.value)}
                placeholder="새 비밀번호"
                className={`${inputClass} pr-11`}
              />
              <button
                type="button"
                onClick={() => setShowPw((v) => !v)}
                aria-label="비밀번호 표시 전환"
                className="absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-md text-foreground-500 hover:bg-background-100"
              >
                <i className={showPw ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
              </button>
            </div>
          </Field>

          <Field label="새 비밀번호 확인" required>
            <input
              type={showPw ? 'text' : 'password'}
              autoComplete="new-password"
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              placeholder="한 번 더 입력"
              className={inputClass}
            />
          </Field>

          {pwError && (
            <div className="flex items-start gap-2 rounded-md bg-accent-100 px-3 py-2.5 text-sm text-accent-900">
              <i className="ri-error-warning-line mt-0.5"></i>
              <span>{pwError}</span>
            </div>
          )}
          {pwNotice && (
            <div className="flex items-start gap-2 rounded-md bg-primary-100 px-3 py-2.5 text-sm text-primary-700">
              <i className="ri-checkbox-circle-line mt-0.5"></i>
              <span>{pwNotice}</span>
            </div>
          )}

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={pwBusy}
              className="flex items-center gap-1.5 rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition hover:bg-primary-600 disabled:opacity-50 whitespace-nowrap"
            >
              {pwBusy ? (
                <i className="ri-loader-4-line animate-spin"></i>
              ) : (
                <i className="ri-lock-2-line"></i>
              )}
              비밀번호 변경
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}